// src/context/OrgBrandContext.js
// Global context for selected organization and brand

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const OrgBrandContext = createContext(null);

export const OrgBrandProvider = ({ children }) => {
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialized, setInitialized] = useState(false);

  // Fetch organizations on mount
  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await axios.get('/api/user/organizations', {
        withCredentials: true
      });
      const orgs = response.data.organizations || [];
      setOrganizations(orgs);
      return orgs;
    } catch (error) {
      // Silently fail if not authenticated (401, 403, 404) - expected for guests and unauthenticated users
      const status = error.response?.status;
      if (status !== 401 && status !== 403 && status !== 404) {
        console.error('Error fetching organizations:', error);
      }
      return [];
    }
  }, []);

  // Fetch brands on mount
  const fetchBrands = useCallback(async () => {
    try {
      const response = await axios.get('/api/brands/list', {
        withCredentials: true
      });
      const allBrands = response.data.brands || [];
      setBrands(allBrands);
      return allBrands;
    } catch (error) {
      // Silently fail if not authenticated (401, 403, 404) - expected for guests and unauthenticated users
      const status = error.response?.status;
      if (status !== 401 && status !== 403 && status !== 404) {
        console.error('Error fetching brands:', error);
      }
      return [];
    }
  }, []);

  // Initialize from database preferences - database is source of truth
  useEffect(() => {
    const initialize = async () => {
      // Prevent multiple initializations
      if (initialized) return;

      setLoading(true);

      try {
        // Skip initialization for guest routes
        if (window.location.pathname.startsWith('/guest')) {
          setLoading(false);
          setInitialized(true);
          return;
        }

        // Add a small delay to ensure session is fully established after OAuth
        await new Promise(resolve => setTimeout(resolve, 100));

        // Fetch organizations and brands in parallel
        const [orgs, allBrands] = await Promise.all([fetchOrganizations(), fetchBrands()]);

        // If no orgs were fetched, wait a bit and retry once
        if (orgs.length === 0) {
          await new Promise(resolve => setTimeout(resolve, 500));
          const [retriedOrgs, retriedBrands] = await Promise.all([fetchOrganizations(), fetchBrands()]);

          if (retriedOrgs.length > 0) {
            // Retry succeeded, use the retried data
            const prefsResponse = await fetchPreferencesWithRetry();
            await restorePreferences(retriedOrgs, retriedBrands, prefsResponse);
          }
        } else {
          // First attempt succeeded
          const prefsResponse = await fetchPreferencesWithRetry();
          await restorePreferences(orgs, allBrands, prefsResponse);
        }
      } catch (error) {
        console.error('Error initializing org/brand context:', error);
      }

      setLoading(false);
      setInitialized(true);
    };

    // Helper to fetch preferences with retry
    const fetchPreferencesWithRetry = async () => {
      try {
        return await axios.get('/api/user/preferences', {
          withCredentials: true
        });
      } catch (error) {
        // If failed, wait and retry once
        if (error.response?.status === 401) {
          await new Promise(resolve => setTimeout(resolve, 300));
          try {
            return await axios.get('/api/user/preferences', {
              withCredentials: true
            });
          } catch (retryError) {
            return null;
          }
        }
        return null;
      }
    };

    // Helper to restore preferences
    const restorePreferences = async (orgs, allBrands, prefsResponse) => {
      // Determine which org/brand IDs to use
      let savedOrgId, savedBrandId;

      if (prefsResponse?.data) {
        // Use database preferences (source of truth)
        savedOrgId = prefsResponse.data.selected_organization_id;
        savedBrandId = prefsResponse.data.selected_brand_id;

        // Update localStorage cache for instant UI updates next time
        if (savedOrgId) {
          localStorage.setItem('selected_organization_id', savedOrgId);
        } else {
          localStorage.removeItem('selected_organization_id');
        }
        if (savedBrandId) {
          localStorage.setItem('selected_brand_id', savedBrandId);
        } else {
          localStorage.removeItem('selected_brand_id');
        }
      } else {
        // Fallback to localStorage if database fetch failed
        savedOrgId = localStorage.getItem('selected_organization_id');
        savedBrandId = localStorage.getItem('selected_brand_id');
      }

      // Restore organization
      let restoredOrg = null;
      if (savedOrgId) {
        const savedOrg = orgs.find(o => o._id === savedOrgId);
        if (savedOrg) {
          setSelectedOrganization(savedOrg);
          restoredOrg = savedOrg;
        }
      }

      // Only restore brand if we have an organization and the brand belongs to that org
      if (restoredOrg && savedBrandId) {
        const savedBrand = allBrands.find(b => (b._id || b.id) === savedBrandId);
        if (savedBrand && savedBrand.organization_id === restoredOrg._id) {
          setSelectedBrand(savedBrand);
        } else {
          // Saved brand doesn't exist or doesn't belong to org - auto-select first brand
          const brandsInOrg = allBrands.filter(b => b.organization_id === restoredOrg._id);
          if (brandsInOrg.length > 0) {
            setSelectedBrand(brandsInOrg[0]);
          }
        }
      } else if (restoredOrg) {
        // Org restored but no saved brand - auto-select first brand
        const brandsInOrg = allBrands.filter(b => b.organization_id === restoredOrg._id);
        if (brandsInOrg.length > 0) {
          setSelectedBrand(brandsInOrg[0]);
        }
      }

      // Auto-select first org/brand if no saved preferences exist
      // This is particularly important for clients in proxy mode who don't have saved preferences
      if (!restoredOrg && orgs.length > 0) {
        const firstOrg = orgs[0];
        setSelectedOrganization(firstOrg);

        // Auto-select first brand within that org
        const brandsInOrg = allBrands.filter(b => b.organization_id === firstOrg._id);
        if (brandsInOrg.length > 0) {
          setSelectedBrand(brandsInOrg[0]);
        }
      }
    };

    initialize();
  }, [fetchOrganizations, fetchBrands, initialized]);

  // Clear brand when organization changes or is cleared, and auto-select first brand if needed
  useEffect(() => {
    if (!selectedOrganization) {
      // No org selected, clear brand from both context and localStorage
      setSelectedBrand(null);
      localStorage.removeItem('selected_brand_id');
    } else {
      // Org is selected
      if (selectedBrand) {
        // Verify selected brand belongs to current organization
        if (selectedBrand.organization_id !== selectedOrganization._id &&
            selectedBrand.organization_id !== selectedOrganization.id) {
          // Brand doesn't belong to current org, auto-select first brand from new org
          const brandsInOrg = brands.filter(b => b.organization_id === selectedOrganization._id);
          if (brandsInOrg.length > 0) {
            setSelectedBrand(brandsInOrg[0]);
            localStorage.setItem('selected_brand_id', brandsInOrg[0]._id || brandsInOrg[0].id);
          } else {
            setSelectedBrand(null);
            localStorage.removeItem('selected_brand_id');
          }
        }
      } else {
        // Org is selected but no brand is selected - auto-select first brand
        const brandsInOrg = brands.filter(b => b.organization_id === selectedOrganization._id);
        if (brandsInOrg.length > 0) {
          setSelectedBrand(brandsInOrg[0]);
          localStorage.setItem('selected_brand_id', brandsInOrg[0]._id || brandsInOrg[0].id);
        }
      }
    }
  }, [selectedOrganization, brands, selectedBrand]);

  // Listen for brand updates
  useEffect(() => {
    const handleBrandUpdate = (event) => {
      const updatedBrand = event.detail;
      if (updatedBrand && selectedBrand &&
          ((updatedBrand._id && updatedBrand._id === selectedBrand._id) ||
           (updatedBrand.id && updatedBrand.id === selectedBrand.id))) {
        setSelectedBrand(updatedBrand);
      }
    };

    window.addEventListener('brand-updated', handleBrandUpdate);
    return () => window.removeEventListener('brand-updated', handleBrandUpdate);
  }, [selectedBrand]);

  // Update selected organization
  const setOrganization = useCallback(async (org, skipPersistence = false) => {
    setSelectedOrganization(org);

    // Skip persistence in proxy mode or when explicitly requested
    if (skipPersistence) {
      return;
    }

    // Optimistic update: Update localStorage immediately for instant UI
    if (org) {
      localStorage.setItem('selected_organization_id', org._id);
    } else {
      localStorage.removeItem('selected_organization_id');
    }

    // Save to database asynchronously (don't await to avoid blocking UI)
    try {
      const response = await axios.post('/api/user/preferences', {
        organization_id: org ? org._id : null
      }, { withCredentials: true });
      console.log('Organization preference saved to database:', org?.organization_name || 'cleared');
    } catch (error) {
      console.error('Failed to save organization preference to database:', error.response?.data || error.message);
      // UI already updated optimistically, so this is non-blocking
    }
  }, []);

  // Update selected brand
  const setBrand = useCallback(async (brand, skipPersistence = false) => {
    setSelectedBrand(brand);

    // Skip persistence in proxy mode or when explicitly requested
    if (skipPersistence) {
      return;
    }

    // Optimistic update: Update localStorage immediately for instant UI
    if (brand) {
      localStorage.setItem('selected_brand_id', brand._id || brand.id);
    } else {
      localStorage.removeItem('selected_brand_id');
    }

    // Save to database asynchronously (don't await to avoid blocking UI)
    try {
      const response = await axios.post('/api/user/preferences', {
        brand_id: brand ? (brand._id || brand.id) : null
      }, { withCredentials: true });
      console.log('Brand preference saved to database:', brand?.brand_name || brand?.name || 'cleared');
    } catch (error) {
      console.error('Failed to save brand preference to database:', error.response?.data || error.message);
      // UI already updated optimistically, so this is non-blocking
    }
  }, []);

  // Clear selection (e.g., when switching agencies)
  const clearSelection = useCallback(async (skipPersistence = false) => {
    setSelectedOrganization(null);
    setSelectedBrand(null);

    // Skip persistence in proxy mode or when explicitly requested
    if (skipPersistence) {
      return;
    }

    // Clear from localStorage
    localStorage.removeItem('selected_organization_id');
    localStorage.removeItem('selected_brand_id');

    // Clear from database asynchronously
    try {
      await axios.post('/api/user/preferences', {
        organization_id: null,
        brand_id: null
      }, { withCredentials: true });
    } catch (error) {
      console.error('Failed to clear preferences from database:', error);
    }
  }, []);

  // Save admin state before entering proxy mode
  const saveAdminState = useCallback(() => {
    const adminState = {
      org: localStorage.getItem('selected_organization_id'),
      brand: localStorage.getItem('selected_brand_id'),
      // Also save the current context state (more reliable than localStorage)
      contextOrg: selectedOrganization?._id || null,
      contextBrand: selectedBrand?._id || selectedBrand?.id || null
    };
    sessionStorage.setItem('admin_state_backup', JSON.stringify(adminState));
  }, [selectedOrganization, selectedBrand]);

  // Restore admin state after exiting proxy mode
  const restoreAdminState = useCallback(() => {
    // Clear proxy session storage when exiting proxy mode
    sessionStorage.removeItem('proxy_selected_org_id');
    sessionStorage.removeItem('proxy_selected_brand_id');

    const backup = sessionStorage.getItem('admin_state_backup');
    if (backup) {
      try {
        const { org, brand, contextOrg, contextBrand } = JSON.parse(backup);

        // Restore from saved context state (more reliable)
        // Use localStorage as fallback
        const orgToRestore = contextOrg || org;
        const brandToRestore = contextBrand || brand;

        if (orgToRestore) {
          localStorage.setItem('selected_organization_id', orgToRestore);
          // Find and set the organization object
          const orgObj = organizations.find(o => o._id === orgToRestore);
          if (orgObj) setSelectedOrganization(orgObj);
        } else {
          localStorage.removeItem('selected_organization_id');
          setSelectedOrganization(null);
        }

        if (brandToRestore) {
          localStorage.setItem('selected_brand_id', brandToRestore);
          // Find and set the brand object
          const brandObj = brands.find(b => (b._id || b.id) === brandToRestore);
          if (brandObj) setSelectedBrand(brandObj);
        } else {
          localStorage.removeItem('selected_brand_id');
          setSelectedBrand(null);
        }
      } catch (e) {
        console.error('Error restoring admin state:', e);
      }
      sessionStorage.removeItem('admin_state_backup');
    }
  }, [organizations, brands]);

  // Clear proxy state from localStorage and sessionStorage
  const clearProxyState = useCallback(() => {
    localStorage.removeItem('selected_organization_id');
    localStorage.removeItem('selected_brand_id');
    sessionStorage.removeItem('proxy_selected_org_id');
    sessionStorage.removeItem('proxy_selected_brand_id');
    setSelectedOrganization(null);
    setSelectedBrand(null);
  }, []);

  // Refresh data
  const refresh = useCallback(async () => {
    await Promise.all([fetchOrganizations(), fetchBrands()]);
  }, [fetchOrganizations, fetchBrands]);

  // Reinitialize preferences (useful for proxy mode)
  const reinitializePreferences = useCallback(async () => {
    try {
      // Fetch organizations and brands in parallel
      const [orgs, allBrands] = await Promise.all([fetchOrganizations(), fetchBrands()]);

      // Fetch user preferences from database (in proxy mode, backend returns proxied user's prefs)
      let prefsResponse;
      try {
        prefsResponse = await axios.get('/api/user/preferences', {
          withCredentials: true
        });
      } catch (error) {
        // Silently fail
        prefsResponse = null;
      }

      // Determine which org/brand IDs to use
      let savedOrgId, savedBrandId;

      if (prefsResponse?.data) {
        savedOrgId = prefsResponse.data.selected_organization_id;
        savedBrandId = prefsResponse.data.selected_brand_id;
      }

      // Restore organization
      let restoredOrg = null;
      if (savedOrgId) {
        const savedOrg = orgs.find(o => o._id === savedOrgId);
        if (savedOrg) {
          setSelectedOrganization(savedOrg);
          restoredOrg = savedOrg;
        }
      } else {
        setSelectedOrganization(null);
      }

      // Only restore brand if we have an organization and the brand belongs to that org
      if (restoredOrg && savedBrandId) {
        const savedBrand = allBrands.find(b => (b._id || b.id) === savedBrandId);
        if (savedBrand && savedBrand.organization_id === restoredOrg._id) {
          setSelectedBrand(savedBrand);
        } else {
          // Saved brand doesn't exist or doesn't belong to org - auto-select first brand
          const brandsInOrg = allBrands.filter(b => b.organization_id === restoredOrg._id);
          if (brandsInOrg.length > 0) {
            setSelectedBrand(brandsInOrg[0]);
          } else {
            setSelectedBrand(null);
          }
        }
      } else if (restoredOrg) {
        // Org restored but no saved brand - auto-select first brand
        const brandsInOrg = allBrands.filter(b => b.organization_id === restoredOrg._id);
        if (brandsInOrg.length > 0) {
          setSelectedBrand(brandsInOrg[0]);
        } else {
          setSelectedBrand(null);
        }
      } else {
        setSelectedBrand(null);
      }
    } catch (error) {
      console.error('Error reinitializing preferences:', error);
    }
  }, [fetchOrganizations, fetchBrands]);

  // Get brands for a specific organization
  const getBrandsForOrg = useCallback((orgId) => {
    return brands.filter(b => b.organization_id === orgId);
  }, [brands]);

  const value = {
    selectedOrganization,
    selectedBrand,
    organizations,
    brands,
    loading,
    setOrganization,
    setBrand,
    clearSelection,
    refresh,
    reinitializePreferences,
    getBrandsForOrg,
    saveAdminState,
    restoreAdminState,
    clearProxyState
  };

  return (
    <OrgBrandContext.Provider value={value}>
      {children}
    </OrgBrandContext.Provider>
  );
};

// Custom hook to use the context
export const useOrgBrand = () => {
  const context = useContext(OrgBrandContext);
  if (!context) {
    throw new Error('useOrgBrand must be used within an OrgBrandProvider');
  }
  return context;
};

export default OrgBrandContext;
